import { useCallback } from 'react';
import classNames from 'classnames/bind';
import { line } from 'd3-shape';
import { scaleTime, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { isFinite } from 'lodash';

import type { Props } from '../components/ForecastGraphConsistencyContainer/ForecastGraphConsistencyContainer';

import styles from '../components/ForecastGraphConsistencyContainer/ForecastGraphConsistencyContainer.module.scss';

const cx = classNames.bind(styles);

const MAX_CONSISTENCY = 100;

interface GraphDataPoint {
  hour: number;
  waveCount: number | null;
}

const useSetupForecastGraphConsistency = ({
  data,
  graphHeight,
  graphWidth,
}: Props & {
  graphHeight?: number;
  graphWidth?: number;
}) => {
  const setupForecastGraphConsistency = useCallback(() => {
    if (graphHeight && graphWidth && isFinite(graphHeight) && isFinite(graphWidth)) {
      select(`.wave-consistency-graph-ticks`).remove();
      select(`.wave-consistency-graph-svg`).remove();

      const graphData: Array<GraphDataPoint> = data.map((dataPoint, index: number) => ({
        ...dataPoint,
        hour: index,
        waveCount: dataPoint?.consistency.waveCount ?? null,
      }));

      const xPadding = 0;
      const yPadding = 2;

      const x = scaleTime()
        .range([xPadding, graphWidth - xPadding])
        .domain([0, data.length - 1]);

      const y = scaleLinear()
        .range([graphHeight - yPadding, yPadding])
        .domain([0, MAX_CONSISTENCY]);

      const path = line<GraphDataPoint>()
        .x((d) => x(d.hour))
        .y((d) => y(d?.waveCount ?? 0));

      const canvas = select(`.wave-consistency-graph-canvas`);

      const tickData = [
        { label: 'Very Inconsistent', position: 20, positionLine: 10 },
        { label: 'Very Consistent', position: 80, positionLine: 90 },
      ];

      const ticks = canvas.append('div').attr('class', 'wave-consistency-graph-ticks');

      ticks
        .selectAll('.wave-consistency-graph-tick')
        .data(tickData)
        .enter()
        .append('div')
        .attr('class', cx(['wave-consistency-graph-tick', styles.tick]))
        .style('top', (d) => `${y(d.position)}px`)
        .text((d) => `${d.label}`);

      ticks
        .selectAll('.wave-consistency-graph-dashed-line')
        .data(tickData)
        .enter()
        .append('div')
        .attr('class', cx(['wave-consistency-graph-dashed-line', styles.dashedLine]))
        .style('top', (d) => `${y(d.positionLine)}px`);

      const svg = canvas
        .append('svg')
        .attr('class', cx(['wave-consistency-graph-svg', styles.svg]));

      const consistencyGroup = svg.append('g').attr('class', 'wave-consistency');

      consistencyGroup
        .append('path')
        .attr('class', styles.visibleLine)
        .attr('d', path(graphData as any));
    }
  }, [data, graphHeight, graphWidth]);

  return { setupForecastGraphConsistency };
};

export default useSetupForecastGraphConsistency;
